/**
 * Accounts page (FE-3, PHASE1 §4 accounts + §7).
 *
 * Three lifecycle actions used to be spread across two dialogs and two
 * collapsibles with overlapping wording. They are now one vocabulary
 * (`lib/labels.ts::accountLifecycleState`) with one entry point each:
 *
 *   Konto schließen   `archived` — kein Abgleich, raus aus dem Nettovermögen,
 *                     Buchungen zählen weiter in Berichten/Budgets.
 *   Ausblenden        `hidden`   — Konto UND Buchungen raus aus allem, umkehrbar.
 *   Entfernen         wipe       — Daten gelöscht, kommt nicht wieder.
 *
 * The card keeps only the everyday actions inline; the three lifecycle actions
 * plus the two rare ones live behind the „…" menu, and all three inactive
 * states share one „Inaktive Konten" section that names the state per row.
 */
import { useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Ellipsis,
  Eye,
  EyeOff,
  Landmark,
  Lock,
  LockOpen,
  Mail,
  Merge,
  Plus,
  Trash2,
  Unlink,
  Wallet,
} from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { AmountText } from '../components/ui/AmountText.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { InlineError } from '../components/ui/InlineError.tsx';
import { Popover } from '../components/ui/Popover.tsx';
import { SkeletonList } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import {
  ACCOUNT_LIFECYCLE,
  ACCOUNT_LIFECYCLE_HINT,
  ACCOUNT_TYPE,
  accountChannelLabel,
  accountLifecycleState,
} from '../lib/labels.ts';
import { formatDateTime, money } from '../lib/format.ts';
import { useAccountsQuery, useHiddenAccountsQuery } from '../lib/hooks/useAccounts.ts';
import {
  useReorderAccounts,
  useUnhideAccount,
  useUpdateAccount,
} from '../lib/hooks/useAccountMutations.ts';
import { useEmailIngestAvailability } from '../lib/hooks/useEmailConfig.ts';
import { ApiRequestError } from '../lib/api.ts';
import type { AccountOut, AccountRemoveMode } from '../lib/types.ts';
import { AccountFormDialog } from './accounts/AccountFormDialog.tsx';
import { BalanceDialog } from './accounts/BalanceDialog.tsx';
import { EmailConfigDialog } from './accounts/EmailConfigDialog.tsx';
import { ManualTransactionDialog } from './accounts/ManualTransactionDialog.tsx';
import { MergeAccountsDialog } from './accounts/MergeAccountsDialog.tsx';
import { RemoveAccountDialog } from './accounts/RemoveAccountDialog.tsx';

function bySort(a: AccountOut, b: AccountOut): number {
  return a.sort_order - b.sort_order || a.id - b.id;
}

/** „Inaktive Konten" ordering: closed (still meaningful) before hidden before
 * removed (a tombstone with nothing left to do). */
const INACTIVE_RANK = { closed: 0, hidden: 1, removed: 2, active: 3 } as const;

function byLifecycle(a: AccountOut, b: AccountOut): number {
  const rank = INACTIVE_RANK[accountLifecycleState(a)] - INACTIVE_RANK[accountLifecycleState(b)];
  return rank || bySort(a, b);
}

export default function AccountsPage() {
  const query = useAccountsQuery();
  const reorder = useReorderAccounts();
  const update = useUpdateAccount();
  // E-Mail-Import (Phase 7): the whole channel is config-gated server-side —
  // the "E-Mail" card action stays hidden until it is enabled (mirrors the
  // FinTS availability gate, `useFintsAvailability`/`FintsSetupPage.tsx`).
  const emailAvailability = useEmailIngestAvailability();
  const emailAvailable = emailAvailability.data?.enabled ?? false;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccountOut | null>(null);
  const [balanceFor, setBalanceFor] = useState<AccountOut | null>(null);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [txnAccountId, setTxnAccountId] = useState<number | null>(null);
  const [closeTarget, setCloseTarget] = useState<AccountOut | null>(null);
  const [detachTarget, setDetachTarget] = useState<AccountOut | null>(null);
  const [emailFor, setEmailFor] = useState<AccountOut | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [mergeFor, setMergeFor] = useState<AccountOut | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [removeFor, setRemoveFor] = useState<AccountOut | null>(null);
  const [removeMode, setRemoveMode] = useState<AccountRemoveMode>('hide');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [unhideError, setUnhideError] = useState<string | null>(null);

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const active = useMemo(() => items.filter((a) => !a.archived).sort(bySort), [items]);
  const closed = useMemo(() => items.filter((a) => a.archived).sort(bySort), [items]);
  const totalBalance = query.data?.totalBalance ?? null;
  // Hidden rows are excluded from `items` server-side, so their count can only
  // come from the response meta — it decides whether the section exists at all,
  // before the lazy hidden query has run.
  const hiddenCount = query.data?.hiddenCount ?? 0;
  const inactiveCount = closed.length + hiddenCount;

  const hiddenQuery = useHiddenAccountsQuery(showInactive);
  const unhide = useUnhideAccount();
  const inactive = useMemo(
    () => [...closed, ...(hiddenQuery.data ?? [])].sort(byLifecycle),
    [closed, hiddenQuery.data],
  );

  function moveAccount(index: number, dir: -1 | 1) {
    const next = [...active];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    reorder.mutate(next.map((a) => a.id));
  }

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(account: AccountOut) {
    setEditing(account);
    setFormOpen(true);
  }

  function openBalance(account: AccountOut) {
    setBalanceFor(account);
    setBalanceOpen(true);
  }

  function openTxn(accountId: number | null) {
    setTxnAccountId(accountId);
    setTxnOpen(true);
  }

  function openEmail(account: AccountOut) {
    setEmailFor(account);
    setEmailOpen(true);
  }

  function openMerge(account: AccountOut) {
    setMergeFor(account);
    setMergeOpen(true);
  }

  function openRemove(account: AccountOut, mode: AccountRemoveMode) {
    setRemoveFor(account);
    setRemoveMode(mode);
    setRemoveOpen(true);
  }

  function handleUnhide(account: AccountOut) {
    setUnhideError(null);
    unhide.mutate(account.id, {
      onError: (err) => {
        setUnhideError(
          err instanceof ApiRequestError
            ? err.message
            : 'Einblenden fehlgeschlagen. Bitte erneut versuchen.',
        );
      },
    });
  }

  /** Re-open a closed account — the reverse of „Konto schließen". Undoing a
   * close is not destructive, so unlike closing it needs no confirm step. */
  function reopen(account: AccountOut) {
    update.mutate({ id: account.id, body: { archived: false } });
  }

  function confirmClose() {
    if (!closeTarget) return;
    update.mutate(
      { id: closeTarget.id, body: { archived: true } },
      { onSettled: () => setCloseTarget(null) },
    );
  }

  /** Multi-source: the name of the account a feeder writes into (badge text).
   * The target can itself be hidden (not in `items`) — fall back to its id. */
  function feederTargetName(account: AccountOut): string | null {
    if (account.feeds_into_account_id == null) return null;
    return (
      items.find((a) => a.id === account.feeds_into_account_id)?.name ??
      `Konto #${account.feeds_into_account_id}`
    );
  }

  function confirmDetachFeeder() {
    if (!detachTarget) return;
    update.mutate(
      { id: detachTarget.id, body: { feeds_into_account_id: null } },
      { onSettled: () => setDetachTarget(null) },
    );
  }

  return (
    <div>
      <PageHeader
        title="Konten"
        subtitle={totalBalance != null ? `Nettovermögen: ${money(totalBalance)}` : undefined}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => openTxn(null)}>
              <Wallet className="h-4 w-4" />
              <span className="hidden sm:inline">Buchung</span>
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Neues Konto</span>
            </Button>
          </>
        }
      />

      <div className="w-full space-y-8 px-4 py-6 sm:px-6">
        {query.isPending ? (
          <SkeletonList rows={4} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : active.length === 0 && inactiveCount === 0 ? (
          <EmptyState
            icon={Landmark}
            title="Noch keine Konten"
            description="Lege dein erstes Konto an, um Salden und Buchungen zu verwalten."
            action={
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Neues Konto
              </Button>
            }
          />
        ) : active.length === 0 ? (
          // Every account is closed/hidden/removed: the list would render as a
          // blank gap above the „Inaktive Konten" section, which reads as broken.
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Kein offenes Konto — alle Konten sind geschlossen, ausgeblendet oder entfernt.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {active.map((account, index) => (
              <AccountCard
                key={account.id}
                account={account}
                isFirst={index === 0}
                isLast={index === active.length - 1}
                reordering={reorder.isPending}
                emailAvailable={emailAvailable}
                onMoveUp={() => moveAccount(index, -1)}
                onMoveDown={() => moveAccount(index, 1)}
                onEdit={() => openEdit(account)}
                onBalance={() => openBalance(account)}
                onBooking={() => openTxn(account.id)}
                onClose={() => setCloseTarget(account)}
                onEmail={() => openEmail(account)}
                // Merging needs a second OPEN account to merge into: the server
                // rejects a closed target (409 „Das Zielkonto ist geschlossen."),
                // and the dialog's picker excludes them — so gating on the raw
                // account count would offer a permanently dead dialog.
                onMerge={active.length > 1 ? () => openMerge(account) : null}
                onHide={() => openRemove(account, 'hide')}
                onRemove={() => openRemove(account, 'delete')}
                feederTarget={feederTargetName(account)}
                onDetachFeeder={
                  account.feeds_into_account_id != null ? () => setDetachTarget(account) : null
                }
              />
            ))}
          </ul>
        )}

        {/* Inactive section: a sibling of the loading/error/empty/populated
            switch above, not nested inside its "populated" arm — hiding or
            closing the LAST open account leaves `active` empty, but the section
            and its restore actions must stay reachable (§1B "fully
            reversible"). */}
        {!query.isPending && !query.isError && inactiveCount > 0 ? (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowInactive((v) => !v)}
              aria-expanded={showInactive}
              className="flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors duration-150 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            >
              {showInactive ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {`Inaktive Konten (${inactiveCount})`}
            </button>
            {showInactive ? (
              <>
                <InlineError message={unhideError} />
                {hiddenQuery.isPending ? (
                  <SkeletonList rows={2} />
                ) : hiddenQuery.isError ? (
                  <ErrorState error={hiddenQuery.error} onRetry={() => void hiddenQuery.refetch()} />
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
                    {inactive.map((account) => (
                      <InactiveAccountRow
                        key={account.id}
                        account={account}
                        busy={unhide.isPending || update.isPending}
                        onReopen={() => reopen(account)}
                        onUnhide={() => handleUnhide(account)}
                        onHide={() => openRemove(account, 'hide')}
                        onRemove={() => openRemove(account, 'delete')}
                      />
                    ))}
                  </ul>
                )}
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <AccountFormDialog open={formOpen} onOpenChange={setFormOpen} account={editing} />
      <BalanceDialog open={balanceOpen} onOpenChange={setBalanceOpen} account={balanceFor} />
      <EmailConfigDialog open={emailOpen} onOpenChange={setEmailOpen} account={emailFor} />
      <MergeAccountsDialog open={mergeOpen} onOpenChange={setMergeOpen} source={mergeFor} />
      <RemoveAccountDialog
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        account={removeFor}
        mode={removeMode}
      />
      <ManualTransactionDialog
        open={txnOpen}
        onOpenChange={setTxnOpen}
        defaultAccountId={txnAccountId}
      />
      <ConfirmDialog
        open={detachTarget !== null}
        onOpenChange={(o) => {
          if (!o) setDetachTarget(null);
        }}
        title="Zweitquelle lösen"
        message={`„${detachTarget?.name}" nicht mehr in „${detachTarget ? feederTargetName(detachTarget) : ''}" schreiben lassen? Der Bankzugang synchronisiert danach wieder in dieses Konto selbst — die letzten Tage können dadurch doppelt erscheinen (erneutes Zusammenführen behebt das).`}
        confirmLabel="Zweitquelle lösen"
        loading={update.isPending}
        onConfirm={confirmDetachFeeder}
      />
      <ConfirmDialog
        open={closeTarget !== null}
        onOpenChange={(o) => {
          if (!o) setCloseTarget(null);
        }}
        title="Konto schließen"
        message={`„${closeTarget?.name}" schließen? Das Konto wird nicht mehr mit der Bank abgeglichen und zählt nicht mehr zum Nettovermögen. Die bisherigen Buchungen bleiben in Berichten und Budgets erhalten.`}
        confirmLabel="Konto schließen"
        loading={update.isPending}
        onConfirm={confirmClose}
      />
    </div>
  );
}

interface MenuAction {
  key: string;
  label: string;
  icon: typeof Trash2;
  onSelect: () => void;
  danger?: boolean;
  /** Rule above this entry — separates lifecycle actions from the rest. */
  separated?: boolean;
}

/**
 * The card's „…" overflow. Everything rare or consequential lives here so the
 * inline row stays the three things done daily; the destructive entry is last
 * and the only red one.
 */
function AccountMenu({ actions, label }: { actions: MenuAction[]; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      className="w-64 p-1"
      trigger={
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
        >
          <Ellipsis className="h-4 w-4" />
        </button>
      }
    >
      <div className="flex flex-col">
        {actions.map((action) => (
          <div key={action.key}>
            {action.separated ? (
              <div className="my-1 h-px bg-slate-100 dark:bg-slate-800" />
            ) : null}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                action.onSelect();
              }}
              className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors duration-150 ${
                action.danger
                  ? 'text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/60'
              }`}
            >
              <action.icon className="h-4 w-4 shrink-0" />
              {action.label}
            </button>
          </div>
        ))}
      </div>
    </Popover>
  );
}

interface AccountCardProps {
  account: AccountOut;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
  /** E-Mail-Import channel availability (Phase 7) — hides the "E-Mail" action
   * entirely while `false`, exactly like FinTS-gated UI elsewhere. */
  emailAvailable: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onEdit: () => void;
  onBalance: () => void;
  onBooking: () => void;
  onClose: () => void;
  onEmail: () => void;
  /** null hides the action (needs a second open account to merge into). */
  onMerge: (() => void) | null;
  onHide: () => void;
  onRemove: () => void;
  /** Multi-source: target name when this account is a feeder (badge text). */
  feederTarget: string | null;
  /** null hides the action (only feeders can be detached). */
  onDetachFeeder: (() => void) | null;
}

/** Renders OPEN accounts only — closed/hidden/removed rows go through
 * `InactiveAccountRow`, so nothing here re-tests the lifecycle flags. */
function AccountCard({
  account,
  isFirst,
  isLast,
  reordering,
  emailAvailable,
  onMoveUp,
  onMoveDown,
  onEdit,
  onBalance,
  onBooking,
  onClose,
  onEmail,
  onMerge,
  onHide,
  onRemove,
  feederTarget,
  onDetachFeeder,
}: AccountCardProps) {
  const actions: MenuAction[] = [];
  if (emailAvailable) {
    actions.push({ key: 'email', label: 'E-Mail-Import…', icon: Mail, onSelect: onEmail });
  }
  if (onMerge) {
    actions.push({ key: 'merge', label: 'Zusammenführen…', icon: Merge, onSelect: onMerge });
  }
  if (onDetachFeeder) {
    actions.push({
      key: 'detach-feeder',
      label: 'Zweitquelle lösen…',
      icon: Unlink,
      onSelect: onDetachFeeder,
    });
  }
  actions.push({
    key: 'close',
    label: 'Konto schließen',
    icon: Lock,
    onSelect: onClose,
    separated: actions.length > 0,
  });
  actions.push({ key: 'hide', label: 'Ausblenden…', icon: EyeOff, onSelect: onHide });
  actions.push({
    key: 'remove',
    label: 'Entfernen…',
    icon: Trash2,
    onSelect: onRemove,
    danger: true,
    separated: true,
  });

  return (
    <li className="py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate font-semibold text-slate-900 dark:text-slate-100">
              {account.name}
            </h2>
            <span
              className={
                account.is_manual
                  ? 'inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                  : 'inline-flex shrink-0 items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700 dark:bg-sky-950/60 dark:text-sky-300'
              }
            >
              {accountChannelLabel(account)}
            </span>
            {feederTarget != null ? (
              <span
                title={`Schreibt Buchungen in „${feederTarget}"`}
                className="inline-flex shrink-0 items-center rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-300"
              >
                {`Zweitquelle für ${feederTarget}`}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            {ACCOUNT_TYPE[account.type]}
            {account.iban ? ` · ${account.iban}` : ''}
          </p>
          <p className="mt-0.5 text-xs text-slate-400 tabular-nums dark:text-slate-500">
            {account.transaction_count} Buchungen
            {account.balance_updated_at
              ? ` · Saldo aktualisiert ${formatDateTime(account.balance_updated_at)}`
              : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {account.current_balance != null ? (
            <AmountText value={account.current_balance} className="text-base" />
          ) : (
            <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1">
        <div className="mr-auto flex items-center gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst || reordering}
            aria-label="Nach oben"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-30 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast || reordering}
            aria-label="Nach unten"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-30 dark:text-slate-500 dark:hover:bg-slate-800/60 dark:hover:text-slate-300"
          >
            <ArrowDown className="h-4 w-4" />
          </button>
        </div>

        <Button variant="ghost" size="sm" onClick={onBooking}>
          Buchung
        </Button>
        {account.is_manual ? (
          <Button variant="ghost" size="sm" onClick={onBalance}>
            Saldo
          </Button>
        ) : null}
        <Button variant="secondary" size="sm" onClick={onEdit}>
          Bearbeiten
        </Button>
        <AccountMenu actions={actions} label={`Weitere Aktionen für ${account.name}`} />
      </div>
    </li>
  );
}

interface InactiveAccountRowProps {
  account: AccountOut;
  busy: boolean;
  onReopen: () => void;
  onUnhide: () => void;
  onHide: () => void;
  onRemove: () => void;
}

/**
 * One row of „Inaktive Konten" — deliberately not the full `AccountCard`: an
 * inactive account has stopped syncing, so only the way back (or further down)
 * applies. Each row names its own state, because the three look identical
 * otherwise and only „Geschlossen" still contributes to reports.
 *
 * A wiped suppression tombstone („Entfernt") gets NO actions at all —
 * un-suppressing it would resurrect an empty ghost account (§3.1/§6).
 */
function InactiveAccountRow({
  account,
  busy,
  onReopen,
  onUnhide,
  onHide,
  onRemove,
}: InactiveAccountRowProps) {
  const state = accountLifecycleState(account);
  return (
    <li className="flex items-start justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h2 className="truncate font-medium text-slate-700 dark:text-slate-300">
            {account.name}
          </h2>
          <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {ACCOUNT_LIFECYCLE[state]}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
          {ACCOUNT_TYPE[account.type]}
          {account.iban ? ` · ${account.iban}` : ''}
        </p>
        <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
          {ACCOUNT_LIFECYCLE_HINT[state]}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {state === 'closed' ? (
          <>
            <Button variant="ghost" size="sm" onClick={onReopen} disabled={busy}>
              <LockOpen className="h-4 w-4" />
              Wieder öffnen
            </Button>
            <AccountMenu
              label={`Weitere Aktionen für ${account.name}`}
              actions={[
                { key: 'hide', label: 'Ausblenden…', icon: EyeOff, onSelect: onHide },
                {
                  key: 'remove',
                  label: 'Entfernen…',
                  icon: Trash2,
                  onSelect: onRemove,
                  danger: true,
                  separated: true,
                },
              ]}
            />
          </>
        ) : null}
        {state === 'hidden' ? (
          <>
            <Button variant="ghost" size="sm" onClick={onUnhide} disabled={busy}>
              <Eye className="h-4 w-4" />
              Wieder einblenden
            </Button>
            <AccountMenu
              label={`Weitere Aktionen für ${account.name}`}
              actions={[
                {
                  key: 'remove',
                  label: 'Entfernen…',
                  icon: Trash2,
                  onSelect: onRemove,
                  danger: true,
                },
              ]}
            />
          </>
        ) : null}
      </div>
    </li>
  );
}
